/**
 * Categoria de uma mídia da OS, derivada do `item_ref` — fonte única.
 *
 * `os_media` guarda toda mídia da OS numa tabela só; o que separa foto de
 * checklist, foto de diagnóstico e registro de execução é a convenção do
 * `item_ref` gravada pelo app do mecânico:
 *
 *   - `front` | `right` | ... ........ foto do checklist de entrada (6 ângulos)
 *   - `markup:<ângulo>` .............. versão rasterizada com as marcações de avaria
 *   - `panel:<painel>` ............... foto de uma peça danificada (módulo funilaria)
 *   - `diag:<itemId>` | `diag:general` mídia de diagnóstico de um item da OS
 *   - `stage:<etapa>` ................ foto da entrada numa etapa (F3.4: `stage:pintura`)
 *   - `before:<peça>` | `after:<peça>`  antes/depois da estética (F2.4 — `before-after.ts`)
 *   - `<itemId>` | null .............. registro de execução
 *
 * Por que isto mora num package e não na API: a mesma regra estava copiada em
 * OITO lugares — `lib/media-category.ts`, o filtro SQL e os rótulos da auditoria
 * de mídia do backoffice, a galeria do cliente B2C, a página pública da OS
 * compartilhada, a confirmação de upload (`POST /media/confirm`), o detalhe de
 * OS do web-admin e a seção de mídia do app. A primeira passada achou cinco;
 * a revisão do #213 achou as outras — incluindo a página pública, que vazaria
 * exatamente como a galeria do B2C.
 *
 * O prefixo `panel:` é exatamente o caso que a centralização existe para
 * cobrir: cópia que não aprende o prefixo novo manda a foto de
 * avaria da entrada para a categoria de EXECUÇÃO, e na galeria do B2C isso é o
 * dono do carro lendo "o que foi feito" e vendo o amassado que já existia
 * quando ele entregou o carro.
 *
 * `panel:` entra aqui ANTES de qualquer app gravar uma foto assim (D13 do plano
 * dos módulos verticais). A ordem é o ponto: leitor que já entende o prefixo
 * quando o primeiro escritor aparece não tem janela de vazamento.
 */

// Pelo subcaminho, e não pelo índice do @mustauto/types: o índice puxa o
// `zod` (address.ts), e quem instala só o próprio workspace — o web-site, no
// Docker — não o tem. Este arquivo não depende de nada.
import { getVehiclePanelLabel, isVehiclePanel, type VehiclePanel } from '@mustauto/types/vehicle-panels';
import { DEFAULT_STAGE_KEYS, isModuleStage, stagePhotoLabel, type WorkshopStageConfig } from '@mustauto/types/modules';
import { beforeAfterLabel } from './before-after';

/** Ângulos do checklist fotográfico de entrada. */
export const CHECKLIST_ANGLES = ['front', 'right', 'left', 'rear', 'dashboard', 'trunk'] as const;

export type MediaCategory = 'checklist' | 'diagnostic' | 'execution';

/** Versão rasterizada com marcações — `markup:front`, etc. */
export const MARKUP_PREFIX = 'markup:';

/** Foto de uma peça danificada — `panel:porta_de`, etc. */
export const PANEL_PREFIX = 'panel:';

/** Mídia de diagnóstico — `diag:<itemId>`. */
export const DIAG_PREFIX = 'diag:';

/** Foto da entrada numa etapa do módulo — `stage:pintura`, etc. (F3.4). */
export const STAGE_PREFIX = 'stage:';

/** Prefixos que, sozinhos, fazem a mídia ser de checklist. */
export const CHECKLIST_PREFIXES = [MARKUP_PREFIX, PANEL_PREFIX] as const;

const ANGLE_SET: ReadonlySet<string> = new Set(CHECKLIST_ANGLES);

/** Foto de uma peça — `panel:<painel>`. */
export function isPanelRef(itemRef: string | null | undefined): boolean {
  return (itemRef ?? '').startsWith(PANEL_PREFIX);
}

/**
 * Rótulo genérico de uma foto de peça — o que aparece quando a chave depois do
 * prefixo não é uma peça da taxonomia (`VEHICLE_PANELS`, em @mustauto/types):
 * gravada por uma versão que conhecia outra lista, ou digitada à mão. Mostrar a
 * chave crua é pior que não nomear a peça: vira rótulo na galeria do cliente e
 * nome de arquivo baixado, com dois-pontos. Para o rótulo de uma foto, use
 * `panelPhotoLabel`.
 */
export const PANEL_PHOTO_LABEL = 'Foto da peça';

/** A peça de uma foto `panel:<peça>`, ou `null` quando não é foto de peça ou a peça é desconhecida. */
export function panelFromRef(itemRef: string | null | undefined): VehiclePanel | null {
  if (!isPanelRef(itemRef)) return null;
  const key = (itemRef ?? '').slice(PANEL_PREFIX.length);
  return isVehiclePanel(key) ? key : null;
}

/**
 * Rótulo de uma foto de peça: o nome da peça ("Porta dianteira esquerda")
 * quando a chave é da taxonomia, senão o genérico {@link PANEL_PHOTO_LABEL}.
 * Nunca devolve a chave crua. É o que a galeria do cliente, o detalhe da OS no
 * painel, a seção de mídia do app e a auditoria do backoffice mostram.
 */
export function panelPhotoLabel(itemRef: string | null | undefined): string {
  const panel = panelFromRef(itemRef);
  return (panel && getVehiclePanelLabel(panel)) ?? PANEL_PHOTO_LABEL;
}

/**
 * O `item_ref` da foto de uma etapa. Quem grava usa isto, e não o texto na
 * mão: o prefixo é o que a regra de visibilidade e os rótulos leem.
 */
export function stageMediaRef(stage: string): string {
  return `${STAGE_PREFIX}${stage}`;
}

export function isStageRef(itemRef: string | null | undefined): boolean {
  return (itemRef ?? '').startsWith(STAGE_PREFIX);
}

/**
 * A etapa de uma foto `stage:<etapa>`, ou `null` quando não é foto de etapa ou
 * a etapa é desconhecida. Conhecida é a padrão de qualquer módulo e, com a
 * configuração da oficina (F3.8), a que ela criou — sem a configuração, a
 * criada é desconhecida e a foto leva o rótulo genérico, nunca a chave crua.
 */
export function stageFromRef(itemRef: string | null | undefined, config?: WorkshopStageConfig): string | null {
  if (!isStageRef(itemRef)) return null;
  const key = (itemRef ?? '').slice(STAGE_PREFIX.length);
  if (DEFAULT_STAGE_KEYS.has(key)) return key;
  const known = config && Object.keys(config).some((module) => isModuleStage(module, key, config));
  return known ? key : null;
}

/** Rótulo genérico de uma foto de etapa desconhecida — nunca a chave crua (ver `PANEL_PHOTO_LABEL`). */
export const STAGE_PHOTO_FALLBACK_LABEL = 'Foto da etapa';

/**
 * Rótulo de uma foto de etapa: "Antes da pintura". É o que a galeria do
 * cliente, a página pública, o painel e o app mostram sob a foto.
 */
export function stageMediaLabel(itemRef: string | null | undefined, config?: WorkshopStageConfig): string {
  const stage = stageFromRef(itemRef, config);
  return stage ? stagePhotoLabel(stage, config) : STAGE_PHOTO_FALLBACK_LABEL;
}

/**
 * A legenda de uma foto de execução que tem nome próprio — "Antes da pintura"
 * (etapa, F3.4), "Antes · Capô" (antes/depois, F2.4) — ou `null`. É o `label`
 * que a galeria do cliente, a página pública e o backoffice mostram sob a foto.
 */
export function executionMediaLabel(
  itemRef: string | null | undefined,
  config?: WorkshopStageConfig,
): string | null {
  if (isStageRef(itemRef)) return stageMediaLabel(itemRef, config);
  return beforeAfterLabel(itemRef);
}

export function isChecklistRef(itemRef: string | null | undefined): boolean {
  const ref = itemRef ?? '';
  return ANGLE_SET.has(ref) || CHECKLIST_PREFIXES.some((p) => ref.startsWith(p));
}

/**
 * A foto de etapa (`stage:`) é EXECUÇÃO, e é de propósito que ela não tem ramo
 * próprio: é o registro do trabalho feito — o reparo pronto para pintar —, que
 * é exatamente o que "o que foi feito" mostra ao cliente. Todo leitor que já
 * existia (galeria do B2C, página pública, painel) a põe no lugar certo sem
 * mudar uma linha; o que muda é só o rótulo (`stageMediaLabel`).
 *
 * O antes/depois da estética (`before:`/`after:`, F2.4) segue a mesma regra,
 * pelo mesmo motivo: quem não conhece o par mostra as duas fotos em "o que foi
 * feito", com o rótulo de `beforeAfterLabel`; quem conhece monta o par.
 */
export function classifyMediaCategory(itemRef: string | null | undefined): MediaCategory {
  if (isChecklistRef(itemRef)) return 'checklist';
  if ((itemRef ?? '').startsWith(DIAG_PREFIX)) return 'diagnostic';
  return 'execution';
}
